/**
 * SF-4656 — one on/off switch on the company page, saved straight away.
 *
 * Turning OFF always asks first. Turning ON asks only when `confirmOn` is
 * given (the chargeable CSAT add-on); otherwise it saves straight from the
 * click. A disabled switch with a reason gets a tooltip (wrapped in a span:
 * a disabled Switch swallows the hover a Tooltip needs).
 *
 * A confirmation that is open when the switch becomes locked (another save
 * started) closes, and its button does nothing while locked: an old open
 * confirmation must not start a second save (review finding).
 */
import { useEffect, useState } from 'react';
import { Popconfirm, Switch, Tooltip } from 'antd';

export interface FlagConfirm {
  title: string;
  description: string;
  okText: string;
}

export interface FlagSwitchProps {
  testId: string;
  ariaLabel: string;
  /** The saved value. */
  checked: boolean;
  /** Non-null while a save of this switch runs: the value it is heading to. */
  pendingValue: boolean | null;
  /** Spinner without a save (e.g. permissions or a check still loading). */
  loading?: boolean;
  disabled: boolean;
  /** Shown as a tooltip when disabled. */
  disabledReason?: string | null;
  confirmOn?: FlagConfirm;
  confirmOff: FlagConfirm;
  danger?: boolean;
  onChange: (next: boolean) => void;
}

export const FlagSwitch: React.FC<FlagSwitchProps> = ({
  testId,
  ariaLabel,
  checked,
  pendingValue,
  loading,
  disabled,
  disabledReason,
  confirmOn,
  confirmOff,
  danger,
  onChange,
}) => {
  const busy = pendingValue !== null;
  const locked = disabled || busy;
  const confirm = checked ? confirmOff : confirmOn;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (locked) setOpen(false);
  }, [locked]);
  const control = (
    <Popconfirm
      title={confirm?.title}
      description={confirm?.description}
      okText={confirm?.okText}
      okButtonProps={checked && danger ? { danger: true } : undefined}
      open={open && !locked}
      onOpenChange={(next) => setOpen(next && !locked)}
      onConfirm={() => {
        setOpen(false);
        if (!locked) onChange(!checked);
      }}
      disabled={locked || !confirm}
    >
      <Switch
        checked={busy ? pendingValue : checked}
        loading={busy || loading}
        disabled={disabled || busy}
        // Without a confirm the click saves at once; with one, the Popconfirm
        // above saves, and the controlled switch stays put until it does.
        onChange={confirm ? undefined : () => onChange(!checked)}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    </Popconfirm>
  );
  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason}>
        <span data-testid={`${testId}-readonly`}>{control}</span>
      </Tooltip>
    );
  }
  return control;
};
